import { useState, useEffect } from 'react';

interface PriceSimulatorProps {
  onCourseSelect: (course: string) => void;
  onPriceUpdate: (price: number) => void;
}

export default function PriceSimulator({ onCourseSelect, onPriceUpdate }: PriceSimulatorProps) {
  const [selectedCourse, setSelectedCourse] = useState('');
  const [carSize, setCarSize] = useState('');
  const [wheelCoating, setWheelCoating] = useState('');
  const [glassCoating, setGlassCoating] = useState('');

  const courses = [
    { id: 'basic', name: '研磨なしコース', basePrice: 21000 },
    { id: 'light', name: '軽研磨コース', basePrice: 30000 },
    { id: 'premium', name: '２層コーティングコース', basePrice: 70000 }
  ];

  const carSizes = [
    { id: 'ss', name: 'SS（軽自動車）', multiplier: { basic: 1.0, light: 1.0, premium: 1.0 } },
    { id: 's', name: 'S（コンパクトカー）', multiplier: { basic: 1.2, light: 1.17, premium: 1.16 } },
    { id: 'm', name: 'M（プリウス、mazda3など）', multiplier: { basic: 1.4, light: 1.33, premium: 1.32 } },
    { id: 'l', name: 'L（大型セダン、中型SUVなど）', multiplier: { basic: 1.6, light: 1.5, premium: 1.48 } },
    { id: 'll', name: 'LL（大型SUV、ミニバン）', multiplier: { basic: 1.8, light: 1.67, premium: 1.68 } }
  ];

  const wheelCoatingOptions = [
    { id: 'wheel-15-17', name: 'ホイールコーティング（〜17インチ）', price: 18000 },
    { id: 'wheel-18-20', name: 'ホイールコーティング（18〜20インチ）', price: 20000 },
    { id: 'wheel-21-plus', name: 'ホイールコーティング（21インチ〜）', price: 23000 }
  ];

  const glassCoatingOptions = [
    { id: 'glass-front', name: '窓ガラスコーティング（フロントガラスのみ）', price: 4000 },
    { id: 'glass-3-sides', name: '窓ガラスコーティング（3面ガラス）', price: 6000 },
    { id: 'glass-all', name: '窓ガラスコーティング（全てのガラス）', price: 11000 }
  ];

  const calculatePrice = () => {
    const course = courses.find(c => c.id === selectedCourse);
    const carSizeData = carSizes.find(c => c.id === carSize);

    if (!course || !carSizeData) return 0;

    // 2層コーティングコースの場合は画像の価格表を使用
    let basePrice;
    if (course.id === 'premium') {
      const premiumPrices = {
        ss: 70000,
        s: 80000,
        m: 90000,
        l: 100000,
        ll: 110000
      };
      basePrice = premiumPrices[carSize as keyof typeof premiumPrices] || course.basePrice;
    } else if (course.id === 'light') {
      // 軽研磨コースの価格表（サイズごとに5000円アップ）
      const lightPrices = {
        ss: 30000,
        s: 35000,
        m: 40000,
        l: 45000,
        ll: 50000
      };
      basePrice = lightPrices[carSize as keyof typeof lightPrices] || course.basePrice;
    } else {
      // その他のコースは従来の計算方法
      const multiplier = carSizeData.multiplier[course.id as keyof typeof carSizeData.multiplier] || 1.0;
      basePrice = course.basePrice * multiplier;
    }
    
    let optionPrice = 0;
    
    // ホイールコーティング料金
    if (wheelCoating) {
      const wheelOption = wheelCoatingOptions.find(o => o.id === wheelCoating);
      optionPrice += wheelOption?.price || 0;
    }
    
    // 窓ガラスコーティング料金
    if (glassCoating) {
      const glassOption = glassCoatingOptions.find(o => o.id === glassCoating);
      optionPrice += glassOption?.price || 0;
    }

    return Math.round(basePrice + optionPrice);
  };

  useEffect(() => {
    const price = calculatePrice();
    onPriceUpdate(price);
    onCourseSelect(courses.find(c => c.id === selectedCourse)?.name || '');
  }, [selectedCourse, carSize, wheelCoating, glassCoating]);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        <i className="ri-calculator-line mr-3 text-blue-600"></i>
        料金シミュレーター
      </h2>

      {/* Course Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          コース選択
        </label>
        <div className="space-y-3">
          {courses.map((course) => (
            <label key={course.id} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="course"
                value={course.id}
                checked={selectedCourse === course.id}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="mr-3"
              />
              <span className="text-gray-800">{course.name}</span>
              <span className="ml-auto text-blue-600 font-semibold">
                {course.basePrice.toLocaleString()}円〜
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Car Size Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          車サイズ
        </label>
        <select
          value={carSize}
          onChange={(e) => setCarSize(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
        >
          <option value="">車サイズを選択してください</option>
          {carSizes.map((size) => (
            <option key={size.id} value={size.id}>
              {size.name}
            </option>
          ))}
        </select>
      </div>

      {/* Wheel Coating Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          ホイールコーティング（18,000円〜）
        </label>
        <select
          value={wheelCoating}
          onChange={(e) => setWheelCoating(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
        >
          <option value="">ホイールコーティングを選択してください</option>
          {wheelCoatingOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name} - {option.price.toLocaleString()}円
            </option>
          ))}
        </select>
      </div>

      {/* Glass Coating Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          窓ガラスコーティング（4,000円〜）
        </label>
        <select
          value={glassCoating}
          onChange={(e) => setGlassCoating(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
        >
          <option value="">窓ガラスコーティングを選択してください</option>
          {glassCoatingOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name} - {option.price.toLocaleString()}円
            </option>
          ))}
        </select>
      </div>

      {/* Price Display */}
      <div className="bg-blue-50 rounded-xl p-6 text-center">
        <div className="text-sm text-gray-600 mb-2">概算料金</div>
        <div className="text-3xl font-bold text-blue-600">
          {calculatePrice() > 0 ? `${calculatePrice().toLocaleString()}円` : '---'}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          ※実際の料金は車の状態により変動する場合があります
        </div>
      </div>
    </div>
  );
}
